import { useState } from 'react';
import { motion } from 'framer-motion';
import { ArrowRight } from 'lucide-react';

const WHATSAPP_NUMBER = '918880331224';

const rooms = [
  {
    id: 'living',
    name: 'Living Room',
    description: 'Elegant living spaces designed for comfort and style',
    designs: [
      {
        id: 1,
        title: 'Modern Minimalist',
        image: 'https://images.unsplash.com/photo-1762544968153-b9b47435fefd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBkYXJrJTIwZ29sZCUyMGFjY2VudHN8ZW58MHx8fHwxNzcyMDEwMTY3fDA&ixlib=rb-4.1.0&q=85',
        price: 'From ₹3.5 Lakh'
      },
      {
        id: 2,
        title: 'Luxury Contemporary',
        image: 'https://images.unsplash.com/photo-1758448755778-90ebf4d0f1e7?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHw0fHxsdXh1cnklMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBkYXJrJTIwZ29sZCUyMGFjY2VudHN8ZW58MHx8fHwxNzcyMDEwMTY3fDA&ixlib=rb-4.1.0&q=85',
        price: 'From ₹5.5 Lakh'
      },
    ]
  },
  {
    id: 'kitchen',
    name: 'Kitchen',
    description: 'Functional kitchens that combine aesthetics with efficiency',
    designs: [
      {
        id: 1,
        title: 'Modular Kitchen',
        image: 'https://images.unsplash.com/photo-1742280879518-ada47b660ccd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1Mjh8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBraXRjaGVuJTIwaXNsYW5kJTIwbWFyYmxlJTIwZGFyayUyMGNhYmluZXRyeXxlbnwwfHx8fDE3NzIwMTAxNjl8MA&ixlib=rb-4.1.0&q=85',
        price: 'From ₹2.5 Lakh'
      },
    ]
  },
  {
    id: 'bedroom',
    name: 'Bedroom',
    description: 'Serene bedrooms designed for rest and relaxation',
    designs: [
      {
        id: 1,
        title: 'Master Suite',
        image: 'https://images.unsplash.com/photo-1760067537116-de1f76fe8f95?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjAzMjd8MHwxfHNlYXJjaHwzfHxtb2Rlcm4lMjBiZWRyb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBsdXh1cnklMjBob3RlbCUyMHN0eWxlfGVufDB8fHx8MTc3MjAxMDE3MXww&ixlib=rb-4.1.0&q=85',
        price: 'From ₹2 Lakh'
      },
    ]
  },
  {
    id: 'office',
    name: 'Home Office',
    description: 'Productive workspace designs for the modern professional',
    designs: [
      {
        id: 1,
        title: 'Executive Office',
        image: 'https://images.pexels.com/photos/4458210/pexels-photo-4458210.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940',
        price: 'From ₹1.5 Lakh'
      },
    ]
  },
  {
    id: 'bathroom',
    name: 'Bathroom',
    description: 'Spa-like bathrooms with premium fixtures',
    designs: [
      {
        id: 1,
        title: 'Luxury Bath',
        image: 'https://images.unsplash.com/photo-1747531301740-7c84b3ecaf23?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1MDV8MHwxfHNlYXJjaHwyfHxtb2Rlcm4lMjBsdXh1cnklMjBob3VzZSUyMGV4dGVyaW9yJTIwZHVzayUyMGFyY2hpdGVjdHVyZSUyMGdvbGQlMjBsaWdodGluZ3xlbnwwfHx8fDE3NzIwMTAxNjV8MA&ixlib=rb-4.1.0&q=85',
        price: 'From ₹1.2 Lakh'
      },
    ]
  },
];

export default function Interiors() {
  const [activeRoom, setActiveRoom] = useState('living');

  const handleEnquiry = (designTitle, roomName) => {
    const message = encodeURIComponent(`Hello! I am interested in the "${designTitle}" ${roomName} design. Please share more details and pricing.`);
    window.open(`https://wa.me/${WHATSAPP_NUMBER}?text=${message}`, '_blank');
  };

  const currentRoom = rooms.find(r => r.id === activeRoom);

  return (
    <div className="noise-overlay pt-24" data-testid="interiors-page">
      {/* Hero Section */}
      <section className="py-16 md:py-24 bg-[#ececec]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="max-w-3xl"
          >
            <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
              Interior Design
            </span>
            <h1 className="font-heading text-5xl md:text-7xl text-gray-900 mt-4">
              Transform Your<br />
              <span className="text-gold-gradient">Living Space</span>
            </h1>
            <p className="text-gray-600 text-lg mt-6 leading-relaxed">
              Explore our curated interior design solutions for every room. 
              From modern minimalist to luxury contemporary – find your style.
            </p>
          </motion.div>
        </div>
      </section>

      {/* Room Categories */}
      <section className="py-8 bg-[#f5f5f5] border-y border-gray-200 sticky top-20 md:top-24 z-30">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="flex items-center gap-4 overflow-x-auto pb-2 scrollbar-hide">
            {rooms.map((room) => (
              <button
                key={room.id}
                onClick={() => setActiveRoom(room.id)}
                className={`px-6 py-3 text-sm font-medium whitespace-nowrap transition-all rounded-xl ${
                  activeRoom === room.id
                    ? 'bg-gradient-to-r from-[#C9A646] to-[#E6C36A] text-[#0A0A0A] shadow-lg shadow-[#C9A646]/30'
                    : 'bg-white text-gray-600 hover:text-gray-900 border border-gray-200 shadow-md shadow-gray-300/50'
                }`}
                data-testid={`room-${room.id}`}
              >
                {room.name}
              </button>
            ))}
          </div>
        </div>
      </section>

      {/* Selected Room Designs */}
      <section className="py-16 md:py-24 bg-[#f5f5f5]">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <motion.div
            key={activeRoom}
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.5 }}
          >
            <div className="mb-12">
              <h2 className="font-heading text-4xl md:text-5xl text-gray-900">
                {currentRoom?.name}
              </h2>
              <p className="text-gray-600 text-lg mt-2">{currentRoom?.description}</p>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
              {currentRoom?.designs.map((design, index) => (
                <motion.div
                  key={design.id}
                  initial={{ opacity: 0, y: 30 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ duration: 0.5, delay: index * 0.1 }}
                  className="group relative overflow-hidden cursor-pointer"
                  onClick={() => handleEnquiry(design.title, currentRoom.name)}
                  data-testid={`design-${design.id}`}
                >
                  <div className="relative h-[400px] md:h-[500px] overflow-hidden">
                    <img 
                      src={design.image}
                      alt={design.title}
                      className="w-full h-full object-cover transform group-hover:scale-110 transition-transform duration-700"
                      loading="lazy"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-[#ececec] via-[#ececec]/20 to-white/0" />
                    
                    <div className="absolute bottom-0 left-0 right-0 p-6 md:p-8">
                      <h3 className="font-heading text-2xl md:text-3xl text-gray-900">
                        {design.title}
                      </h3>
                      <p className="text-[#C9A646] font-medium mt-2">{design.price}</p>
                      
                      <button className="mt-4 btn-secondary text-sm inline-flex items-center gap-2 opacity-0 group-hover:opacity-100 transform translate-y-4 group-hover:translate-y-0 transition-all duration-300">
                        Get This Design <ArrowRight size={16} />
                      </button>
                    </div>
                  </div>
                </motion.div>
              ))}
            </div>
          </motion.div>
        </div>
      </section>

      {/* Full Home Package */}
      <section className="py-16 md:py-24 bg-[#ececec] border-t border-gray-200">
        <div className="max-w-7xl mx-auto px-6 md:px-12">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
            >
              <span className="text-[#C9A646] text-xs uppercase tracking-[0.2em] font-bold">
                Complete Solution
              </span>
              <h2 className="font-heading text-4xl md:text-5xl text-gray-900 mt-4">
                Full Home Interior Packages
              </h2>
              <p className="text-gray-600 text-lg mt-6 leading-relaxed">
                Get a complete interior solution for your entire home. Our packages include 
                design, materials, and installation – all at competitive prices.
              </p>
              
              <div className="mt-8 space-y-4">
                {[
                  { name: '1 BHK Package', price: 'From ₹4 Lakh' },
                  { name: '2 BHK Package', price: 'From ₹6 Lakh' },
                  { name: '3 BHK Package', price: 'From ₹8 Lakh' },
                  { name: '4 BHK Package', price: 'From ₹12 Lakh' },
                ].map((pkg) => (
                  <div 
                    key={pkg.name}
                    className="flex items-center justify-between p-4 bg-white/50 border border-gray-200 hover:border-[#C9A646]/30 transition-colors"
                  >
                    <span className="text-gray-900 font-medium">{pkg.name}</span>
                    <span className="text-[#C9A646]">{pkg.price}</span>
                  </div>
                ))}
              </div>

              <button
                onClick={() => handleEnquiry('Full Home', 'Interior Package')}
                className="mt-8 btn-primary inline-flex items-center gap-2"
                data-testid="package-cta-btn"
              >
                Get Custom Quote
                <ArrowRight size={18} />
              </button>
            </motion.div>

            <motion.div
              initial={{ opacity: 0, x: 30 }}
              whileInView={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
              viewport={{ once: true }}
              className="relative"
            >
              <img 
                src="https://images.unsplash.com/photo-1762544968153-b9b47435fefd?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjY2NzN8MHwxfHNlYXJjaHwzfHxsdXh1cnklMjBtb2Rlcm4lMjBsaXZpbmclMjByb29tJTIwaW50ZXJpb3IlMjBkZXNpZ24lMjBkYXJrJTIwZ29sZCUyMGFjY2VudHN8ZW58MHx8fHwxNzcyMDEwMTY3fDA&ixlib=rb-4.1.0&q=85"
                alt="Full Home Interior"
                className="w-full h-[500px] object-cover"
              />
              <div className="absolute -bottom-6 -left-6 bg-gradient-to-r from-[#C9A646] to-[#E6C36A] p-6">
                <span className="font-heading text-3xl text-[#0A0A0A]">10+</span>
                <p className="text-[#0A0A0A] text-sm font-semibold">Design Styles</p>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-16 md:py-24 bg-gradient-to-r from-[#C9A646] to-[#E6C36A]">
        <div className="max-w-4xl mx-auto px-6 md:px-12 text-center">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            viewport={{ once: true }}
          >
            <h2 className="font-heading text-4xl md:text-5xl text-[#0A0A0A]">
              Design Your Dream Interior
            </h2>
            <p className="text-[#0A0A0A]/80 text-lg mt-4">
              Book a free consultation with our interior designers today.
            </p>
            <button
              onClick={() => handleEnquiry('Interior Design', 'Consultation')}
              className="mt-8 bg-[#ececec] text-gray-900 font-semibold px-10 py-5 hover:bg-white transition-colors inline-flex items-center gap-2"
              data-testid="interiors-cta-btn"
            >
              Book Free Consultation
              <ArrowRight size={20} />
            </button>
          </motion.div>
        </div>
      </section>
    </div>
  );
}
